import type {TabIconVariant} from 'platform/components';
import {match} from 'ts-pattern';
import type {ZodIssue} from 'zod';

import type {FieldErrors} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  getCheckoutProfileDocumentCells,
  getCheckoutProfileDocumentCellKey,
  normalizeCheckoutProfileDraft,
  type SaleRegime,
} from '@dms/api/sales';

import type {Nullish} from 'shared/web';

import type {CheckoutProfileIssue} from '../../../utils/getCheckoutProfileIssueMessage';
import {CheckoutProfileFormSchema} from '../types/CheckoutProfileFormSchema';
import type {CheckoutProfileFormValues} from '../types/CheckoutProfileFormValuesSchema';
import {getCheckoutProfileMissingRequiredFields} from './getCheckoutProfileMissingRequiredFields';

type StatusContext = {
  profile: CheckoutProfileFormValues;
  issues: ZodIssue[];
  errors?: FieldErrors<CheckoutProfileFormValues>;
  missingRequiredFields: ReturnType<typeof getCheckoutProfileMissingRequiredFields>;
};

const getStatus = (invalid: boolean, pending: boolean): TabIconVariant =>
  match({invalid, pending})
    .with({invalid: true}, () => 'error' as const)
    .with({pending: true}, () => 'pending' as const)
    .otherwise(() => 'success' as const);

const aggregateStatus = (statuses: TabIconVariant[]) =>
  getStatus(statuses.includes('error'), statuses.includes('pending'));

const getServerStatus = (issue: CheckoutProfileIssue): TabIconVariant =>
  issue.severity === 'error' ? 'error' : 'pending';

const isDefaultInvalid = (allowed: string[], value: string | Nullish) =>
  isNotNil(value) && value.length > 0 && allowed.length > 0 && !allowed.includes(value);

const isDocumentInvalid = (row: CheckoutProfileFormValues['documents'][number]) =>
  (row.restrictSeries === true && row.allowedSeriesIds.length === 0) ||
  (row.restrictTemplates === true && row.allowedTemplateIds.length === 0) ||
  isDefaultInvalid(row.allowedSeriesIds, row.defaultSeriesId) ||
  isDefaultInvalid(row.allowedTemplateIds, row.defaultTemplateId);

const getGeneralStatus = ({profile, issues, errors}: StatusContext) => {
  const internal = profile.typeOfSale === 'INTERNAL';
  const foreignCurrencyMissing = Boolean(
    !internal &&
      profile.foreignCurrency?.isAllowed &&
      profile.foreignCurrency.isDefault &&
      isNilOrEmpty(profile.foreignCurrency.defaultCurrency)
  );

  const invalid = issues.some(({path}) =>
    match(path[0])
      .with('name', () => isNotNilOrEmpty(profile.name?.trim()))
      .with('branchId', () => isNotNilOrEmpty(profile.branchId))
      .with('typeOfSale', () => isNotNil(profile.typeOfSale))
      .with('foreignCurrency', () => !(path[1] === 'defaultCurrency' && foreignCurrencyMissing))
      .with('isActive', 'saleRegimes', () => true)
      .otherwise(() => false)
  );

  return getStatus(
    invalid ||
      isNotNil(errors?.name) ||
      isNotNil(errors?.branchId) ||
      isNotNil(errors?.typeOfSale) ||
      isNotNil(errors?.isActive) ||
      (!internal && (isNotNil(errors?.foreignCurrency) || isNotNil(errors?.saleRegimes))),
    isNilOrEmpty(profile.name?.trim()) ||
      isNilOrEmpty(profile.branchId) ||
      isNil(profile.typeOfSale) ||
      foreignCurrencyMissing
  );
};

const getPaymentsStatus = ({profile, issues, errors, missingRequiredFields}: StatusContext) => {
  if (profile.typeOfSale === 'INTERNAL') {
    return 'success' as const;
  }

  const methods = profile.payments?.methods ?? [];

  return getStatus(
    isNotNil(errors?.payments) ||
      issues.some(
        ({path}) =>
          path[0] === 'payments' &&
          !missingRequiredFields.some((missing) => missing.join('.') === path.join('.'))
      ) ||
      (isNotNil(profile.payments?.defaultMethod) &&
        !methods.some(({method}) => method === profile.payments?.defaultMethod)),
    methods.length === 0 ||
      isNil(profile.payments?.defaultMethod) ||
      missingRequiredFields.some((path) => path[0] === 'payments')
  );
};

const getDepositStatus = ({profile, issues, errors, missingRequiredFields}: StatusContext) => {
  if (profile.typeOfSale === 'INTERNAL' || !profile.deposit?.isAllowed) {
    return 'success' as const;
  }

  const paymentMethods = profile.payments?.methods ?? [];
  const methods = profile.deposit.methods ?? [];

  return getStatus(
    isNotNil(errors?.deposit) ||
      issues.some(
        ({path}) =>
          path[0] === 'deposit' &&
          !missingRequiredFields.some((missing) => missing.join('.') === path.join('.'))
      ) ||
      methods.some((method) => !paymentMethods.some((payment) => payment.method === method)) ||
      (isNotNil(profile.deposit.defaultMethod) && !methods.includes(profile.deposit.defaultMethod)),
    methods.length === 0 ||
      isNil(profile.deposit.defaultMethod) ||
      (methods.includes('BANK_TRANSFER') && isNil(profile.deposit.maturityDays))
  );
};

export function getCheckoutProfileConfigurationStatus(
  profile: CheckoutProfileFormValues,
  errors?: FieldErrors<CheckoutProfileFormValues>,
  serverIssues: CheckoutProfileIssue[] = [],
  isServerVerified = false
): {
  tabs: Record<'GENERAL' | 'PAYMENTS' | 'DEPOSIT' | 'DOCUMENTS', TabIconVariant>;
  regimes: Record<SaleRegime, TabIconVariant>;
} {
  const internal = profile.typeOfSale === 'INTERNAL';
  const normalized = normalizeCheckoutProfileDraft(profile);
  const cells = getCheckoutProfileDocumentCells(normalized);
  const applicable = new Set(cells.map(getCheckoutProfileDocumentCellKey));
  const rows = profile.documents.filter((row) =>
    applicable.has(getCheckoutProfileDocumentCellKey(row))
  );

  const parsed = CheckoutProfileFormSchema.safeParse({...normalized, documents: rows});
  const issues = parsed.success ? [] : parsed.error.issues;
  const missingRequiredFields = getCheckoutProfileMissingRequiredFields(normalized);
  const context = {profile, issues, errors, missingRequiredFields};
  const localStatus = (status: TabIconVariant) =>
    isServerVerified && status === 'pending' ? ('success' as const) : status;

  const rowStatuses = new Map<string, TabIconVariant>();
  rows.forEach((row, index) => {
    const key = getCheckoutProfileDocumentCellKey(row);
    const originalIndex = profile.documents.indexOf(row);
    const invalid =
      isDocumentInvalid(row) ||
      isNotNil(errors?.documents?.[originalIndex]) ||
      issues.some(({path}) => path[0] === 'documents' && path[1] === index) ||
      rowStatuses.has(key);

    rowStatuses.set(key, getStatus(invalid, false));
  });

  const getRegimeStatus = (regime: SaleRegime, visible: boolean): TabIconVariant => {
    if (internal || !visible) {
      return 'success';
    }

    const statuses = cells
      .filter((cell) => cell.saleRegime === regime)
      .map((cell) => rowStatuses.get(getCheckoutProfileDocumentCellKey(cell)) ?? 'success');

    const serverStatuses = serverIssues
      .filter((issue) => issue.tab === 'DOCUMENTS' && issue.saleRegime === regime)
      .map(getServerStatus);

    return aggregateStatus([
      localStatus(statuses.length > 0 ? aggregateStatus(statuses) : 'pending'),
      ...serverStatuses,
    ]);
  };

  const regimes = {
    DOMESTIC: getRegimeStatus('DOMESTIC', true),
    IN_EUROPE: getRegimeStatus('IN_EUROPE', Boolean(profile.saleRegimes?.inEurope)),
    OUT_OF_EUROPE: getRegimeStatus('OUT_OF_EUROPE', Boolean(profile.saleRegimes?.outOfEurope)),
  };

  const documentStatuses = internal
    ? cells.map((cell) => rowStatuses.get(getCheckoutProfileDocumentCellKey(cell)) ?? 'success')
    : Object.values(regimes);

  return {
    tabs: {
      GENERAL: aggregateStatus([
        localStatus(getGeneralStatus(context)),
        ...serverIssues.filter((issue) => issue.tab === 'GENERAL').map(getServerStatus),
      ]),
      PAYMENTS: aggregateStatus([
        localStatus(getPaymentsStatus(context)),
        ...serverIssues.filter((issue) => issue.tab === 'PAYMENTS').map(getServerStatus),
      ]),
      DEPOSIT: aggregateStatus([
        localStatus(getDepositStatus(context)),
        ...serverIssues.filter((issue) => issue.tab === 'DEPOSIT').map(getServerStatus),
      ]),
      DOCUMENTS: aggregateStatus([
        ...documentStatuses,
        ...serverIssues.filter((issue) => issue.tab === 'DOCUMENTS').map(getServerStatus),
        isNotNil(errors?.documents?.root) ||
        isNotNil(errors?.documents?.type) ||
        isNotNil(errors?.documentTypes)
          ? 'error'
          : 'success',
        issues.some(
          ({path}) => path[0] === 'documentTypes' || (path[0] === 'documents' && isNil(path[1]))
        )
          ? 'error'
          : 'success',
      ]),
    },
    regimes,
  };
}
